import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { CalendarDays, Plus } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { CompactTableDate } from '@/components/common/compact-table-date'
import { jobsApi } from '@/services/jobs'
import { withServiceInstanceId } from '@/lib/service-instance'
import { withReturnTo } from '@/lib/return-navigation'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import type { Job } from '@/types'

type JobSubEventsPanelProps = {
  parentEventId: string
  serviceInstanceId?: string | null
  returnPath?: string | null
}

const statusBadgeClass = (status?: string) => {
  switch (status) {
    case 'completed':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700'
    case 'in_progress':
      return 'border-amber-200 bg-amber-50 text-amber-700'
    case 'cancelled':
      return 'border-rose-200 bg-rose-50 text-rose-700'
    case 'on_hold':
      return 'border-orange-200 bg-orange-50 text-orange-700'
    default:
      return 'border-slate-200 bg-slate-50 text-slate-700'
  }
}

export function JobSubEventsPanel({
  parentEventId,
  serviceInstanceId,
  returnPath,
}: JobSubEventsPanelProps) {
  const labels = useWorkspaceLabels()
  const createSubEventPath = withReturnTo(
    withServiceInstanceId(
      `${labels.basePath}/create?parent_event_id=${encodeURIComponent(parentEventId)}`,
      serviceInstanceId,
    ),
    returnPath,
  )

  const subEventsQuery = useQuery({
    queryKey: ['sub-events', parentEventId, serviceInstanceId ?? 'none'],
    queryFn: () => jobsApi.getSubEvents(parentEventId, {
      service_instance_id: serviceInstanceId ?? undefined,
      page: 1,
      page_size: 100,
      ordering: '-updated_at',
    }),
    enabled: Boolean(parentEventId),
    staleTime: 30_000,
  })

  const subEvents = subEventsQuery.data?.results ?? []

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base">Sub Events</CardTitle>
            <CardDescription>
              Child events linked to this {labels.entityLower}. Each sub event is a full event with its own schedule, assignments, and event days.
            </CardDescription>
          </div>
          <Button asChild>
            <Link to={createSubEventPath}>
              <Plus className="mr-2 h-4 w-4" />
              Add Sub Event
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {subEventsQuery.isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-12 w-full rounded-xl" />
            ))}
          </div>
        ) : subEvents.length === 0 ? (
          <div className="flex min-h-40 flex-col items-center justify-center rounded-2xl border border-dashed border-border/70 bg-muted/10 p-8 text-center">
            <CalendarDays className="mb-2 h-8 w-8 text-muted-foreground/70" />
            <p className="font-medium text-foreground">No sub events yet</p>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">
              Add a sub event to break this {labels.entityLower} into smaller scheduled pieces.
            </p>
            <Button className="mt-4" asChild>
              <Link to={createSubEventPath}>
                <Plus className="mr-2 h-4 w-4" />
                Add Sub Event
              </Link>
            </Button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border/70">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/35 hover:bg-muted/35">
                  <TableHead className="pl-4">Event</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Landmark</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead className="pr-4 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subEvents.map((subEvent: Job) => {
                  const detailPath = withReturnTo(
                    withServiceInstanceId(`${labels.basePath}/${subEvent.id}`, serviceInstanceId),
                    returnPath,
                  )

                  return (
                    <TableRow key={subEvent.id}>
                      <TableCell className="pl-4 font-medium">
                        <Link to={detailPath} className="text-primary hover:underline">
                          {formatJobTitleWithId({
                            jobId: subEvent.id,
                            title: subEvent.title,
                            jobRef: subEvent.job_ref,
                            entityLabel: labels.entity,
                          })}
                        </Link>
                      </TableCell>
                      <TableCell>{subEvent.job_code || '-'}</TableCell>
                      <TableCell>{subEvent.job_landmark || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={statusBadgeClass(subEvent.status)}>
                          {subEvent.status.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <CompactTableDate value={subEvent.start_date ?? subEvent.scheduled_start} includeTime includeYear={false} />
                      </TableCell>
                      <TableCell className="pr-4 text-right">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={detailPath}>Open</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
